import CreateRubricAIDialog from 'components/CreateAssignment/CreateRubricAIDialog';
import CreateRubricDialog from 'components/CreateAssignment/CreateRubricDialog';
import EditBaselineDialog from 'components/CreateAssignment/EditBaselineDialog';
import EditContentBankResourcesDialog from 'components/CreateAssignment/EditContentBankResourcesDialog';
import EditGradingSettingsDialog from 'components/CreateAssignment/EditGradingSettingsDialog';
import EditRubricDialog from 'components/CreateAssignment/EditRubricDialog';
import FeedbackSettingsDialog from 'components/CreateAssignment/FeedbackSettingsDialog';
import PreviewFeedbackDialog from 'components/CreateAssignment/PreviewFeedbackDialog';
import PreviewGradingDialog from 'components/CreateAssignment/PreviewGradingDialog';
import UploadContentBankResourcesDialog from 'components/CreateAssignment/UploadContentBankResourcesDialog';
import UploadPreviewDialog from 'components/CreateAssignment/UploadPreviewDialog';
import SelectRubricDialog from 'components/Rubrics/SelectRubricDialog';
import { useApp } from 'contexts/app';
import { useAppBar } from 'contexts/appBar';
import { useViewport } from 'contexts/viewport';
import { cloneDeep, omit } from 'lodash';
import { PageContext as CreatePageContext } from 'pages/CreateAssignment/PageProvider';
import Desktop from 'pages/CreateAssignment/desktop';
import Mobile from 'pages/CreateAssignment/mobile';
import Tablet from 'pages/CreateAssignment/tablet';
import { PageContext as EditPageContext } from 'pages/EditAssignment/PageProvider';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loading, SplitButton, Stack, VideoDialog } from 'shell/components';

const PageLayout = ({ location }) => {
    const { isLoading, assignments, batch, submissions } = useApp();
    const { setNode } = useAppBar();
    const params = useParams();
    const { device } = useViewport();

    const [videoDialog, setVideoDialog] = useState({ open: false, videoId: '' });

    const PageContext = location.pathname === '/assignments/new' ? CreatePageContext : EditPageContext;
    const props = useContext(PageContext);
    const {
        form,
        setForm,
        setErrors,
        selectRubricDialog,
        setSelectRubricDialog,
        createRubricDialog,
        setCreateRubricDialog,
        createRubricAIDialog,
        setCreateRubricAIDialog,
        editRubricDialog,
        setEditRubricDialog,
        editBaselineDialog,
        setEditBaselineDialog,
        uploadContentBankResourcesDialog,
        setUploadContentBankResourcesDialog,
        editContentBankResourcesDialog,
        setEditContentBankResourcesDialog,
        previewUploadDialog,
        setPreviewUploadDialog,
        previewFeedbackDialog,
        setPreviewFeedbackDialog,
        feedbackSettingsDialog,
        setFeedbackSettingsDialog,
        gradingSettingsDialog,
        setGradingSettingsDialog,
        previewGradingDialog,
        setPreviewGradingDialog,
        isFeedbackSettingsDefault,
        onClickSave,
        onClickUploadPreview,
    } = props;

    const assignment = useMemo(() => assignments?.find(({ id }) => id === params.id), [assignments, params.id]);

    const openVideoAssignmentCreation = useCallback(() => {
        setVideoDialog({ open: true, videoId: 'zSIaLCoDNfQ' });
    }, []);

    const openVideoFeedbackSettings = useCallback(() => {
        setVideoDialog({ open: true, videoId: 'fdUqnMyB6Rc' });
    }, []);

    const openVideoGradingSettings = () => {
        // TODO: video doesn't exist yet, replace with actual implementation when the video is made
        // setVideoDialog({ open: true, videoId: '' });
    };

    const closeSelectRubricDialog = useCallback(() => setSelectRubricDialog({ open: false }), [setSelectRubricDialog]);

    const handleClickAddRubric = useCallback(() => setCreateRubricDialog({ open: true }), [setCreateRubricDialog]);

    const handleClickUseRubric = useCallback(
        (rubric) => {
            setEditRubricDialog({
                open: true,
                customDefaultForm: {
                    ...rubric.rubric,
                    title: '',
                },
            });
        },
        [setEditRubricDialog]
    );

    const closeCreateRubricDialog = useCallback(() => setCreateRubricDialog({ open: false }), [setCreateRubricDialog]);

    const handleClickCreateAI = useCallback(() => setCreateRubricAIDialog({ open: true }), [setCreateRubricAIDialog]);

    const handleClickCreateManual = useCallback(
        () => setEditRubricDialog({ open: true, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const closeCreateRubricAIDialog = useCallback(
        () => setCreateRubricAIDialog({ open: false }),
        [setCreateRubricAIDialog]
    );

    const closeEditRubricDialog = useCallback(
        () => setEditRubricDialog({ open: false, customDefaultForm: null }),
        [setEditRubricDialog]
    );

    const handleSaveRubric = useCallback(
        (rubric) => {
            setForm((current) => {
                const criterionIds = rubric.criteria.map(({ id }) => id);
                let newForm = cloneDeep({ ...current, rubric });

                if (current.baseline) {
                    setErrors((current) => ({ ...current, baseline: '(needs update)' }));
                    newForm.baseline = { ...newForm.baseline, criteria: {} };
                }

                if (current.feedbackSettings.structure === 'templated') {
                    let newTemplated = cloneDeep(current.feedbackSettings.templated);

                    // if rubric criteria or options were removed, remove them from feedbackSettings.templated
                    Object.keys(newTemplated).forEach((criterionId) => {
                        if (!criterionIds.includes(criterionId)) {
                            delete newTemplated[criterionId];
                        } else {
                            const optionIds = rubric.criteria
                                .find(({ id }) => id === criterionId)
                                .options.map(({ id }) => id);
                            Object.keys(newTemplated[criterionId]).forEach((optionId) => {
                                if (!optionIds.includes(optionId)) {
                                    delete newTemplated[criterionId][optionId];
                                }
                            });
                        }
                    });

                    // check if any criteria or options were added
                    const wereNoAdditionsMade = rubric.criteria.every(({ id: criterionId, options }) => {
                        return (
                            Object.keys(newTemplated).includes(criterionId) &&
                            options.every(({ id: optionId }) => {
                                return Object.keys(newTemplated[criterionId]).includes(optionId);
                            })
                        );
                    });

                    if (!wereNoAdditionsMade) {
                        setErrors((current) => ({ ...current, feedbackSettings: '(needs update)' }));
                    }

                    newForm.feedbackSettings.templated = newTemplated;
                }

                let newGradingSettings = cloneDeep(current.gradingSettings);
                if (newGradingSettings) {
                    // remove keys that have been removed from the rubric
                    Object.keys(newGradingSettings.criteria).forEach((criterionId) => {
                        if (!criterionIds.includes(criterionId)) {
                            delete newGradingSettings.criteria[criterionId];
                        }
                    });

                    // check if any criteria were added
                    const wereNoAdditionsMade = rubric.criteria.every(({ id: criterionId }) => {
                        return Object.keys(newGradingSettings.criteria).includes(criterionId);
                    });

                    if (!wereNoAdditionsMade) {
                        setErrors((current) => ({ ...current, gradingSettings: '(needs update)' }));
                    }
                }

                return newForm;
            });
        },
        [setErrors, setForm]
    );

    const closeEditBaselineDialog = useCallback(() => setEditBaselineDialog({ open: false }), [setEditBaselineDialog]);

    const handleSaveBaseline = useCallback(
        (baseline) => {
            setForm((current) => ({ ...current, baseline }));
            setErrors((current) => omit(current, ['baseline']));
        },
        [setErrors, setForm]
    );

    const closeUploadContentBankResourcesDialog = useCallback(
        () => setUploadContentBankResourcesDialog({ open: false }),
        [setUploadContentBankResourcesDialog]
    );

    const onClickUploadContentBankResources = useCallback(
        (files) => {
            setForm((current) => ({
                ...current,
                contentBankResources: [...current.contentBankResources, ...files],
            }));
            setEditContentBankResourcesDialog({ open: true });
        },
        [setEditContentBankResourcesDialog, setForm]
    );

    const closeEditContentBankResourcesDialog = useCallback(
        () => setEditContentBankResourcesDialog({ open: false }),
        [setEditContentBankResourcesDialog]
    );

    const handleSaveContentBankResources = useCallback(
        (contentBankResources) => setForm((current) => ({ ...current, contentBankResources })),
        [setForm]
    );

    const closeUploadPreviewDialog = useCallback(
        () => setPreviewUploadDialog({ open: false, target: null }),
        [setPreviewUploadDialog]
    );

    const closePreviewFeedbackDialog = useCallback(
        () => setPreviewFeedbackDialog({ open: false, output: null }),
        [setPreviewFeedbackDialog]
    );

    const closePreviewGradingDialog = useCallback(
        () => setPreviewGradingDialog({ open: false, output: null }),
        [setPreviewGradingDialog]
    );

    const closeFeedbackSettingsDialog = useCallback(
        () => setFeedbackSettingsDialog({ open: false }),
        [setFeedbackSettingsDialog]
    );

    const handleSaveFeedbackSettings = useCallback(
        (value) => {
            const { feedbackCustomContext } = value;
            value = omit(value, ['feedbackCustomContext']);
            setForm((current) => {
                return value.structure === 'templated'
                    ? {
                          ...current,
                          feedbackSettings: value,
                          aiGradingEnabled: true,
                          aiStudentFeedbackEnabled: false,
                          feedbackCustomContext,
                      }
                    : {
                          ...current,
                          feedbackSettings: value,
                          aiGradingEnabled: true,
                          feedbackCustomContext,
                      };
            });
            setErrors((current) => omit(current, ['feedbackSettings']));
        },
        [setErrors, setForm]
    );

    const closeGradingSettingsDialog = useCallback(
        () => setGradingSettingsDialog({ open: false }),
        [setGradingSettingsDialog]
    );

    const handleSaveGradingSettings = useCallback(
        (value) => {
            const { gradingCustomContext } = value;
            const criteriaGradingInstructions = Object.entries(value.criteria).reduce(
                (acc, [criterionId, { grading_instructions }]) => {
                    return {
                        ...acc,
                        [criterionId]: { grading_instructions: grading_instructions || '' },
                    };
                },
                {}
            );
            const hasCriteriaGradingInstructions = Boolean(
                Object.values(criteriaGradingInstructions)
                    .map(({ grading_instructions }) => grading_instructions)
                    .reduce((acc, cur) => acc + cur, '')
            );
            setForm((current) => {
                return {
                    ...current,
                    gradingCustomContext,
                    gradingSettings: hasCriteriaGradingInstructions ? { criteria: criteriaGradingInstructions } : null,
                };
            });
            setErrors((current) => omit(current, ['gradingSettings']));
        },
        [setErrors, setForm]
    );

    const closeVideoDialog = useCallback(() => setVideoDialog({ open: false, videoId: '' }), []);

    useEffect(() => {
        setNode(
            <Stack direction={'row'} justifyContent={'flex-end'}>
                <SplitButton
                    options={
                        assignment?.is_draft || location.pathname.includes('/assignments/new')
                            ? [
                                  { key: 'publish', value: 'Publish' },
                                  { key: 'save', value: 'Save as Draft' },
                              ]
                            : [
                                  { key: 'publish', value: 'Save' },
                                  { key: 'save', value: 'Unpublish' },
                              ]
                    }
                    onClick={onClickSave}
                />
            </Stack>
        );

        return () => setNode(null);
    }, [assignment, location.pathname, setNode, onClickSave]);

    const renderResponsiveLayout = (device) => {
        return {
            mobile: (
                <Mobile
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...props}
                />
            ),
            tablet: (
                <Tablet
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...props}
                />
            ),
            desktop: (
                <Desktop
                    batch={batch}
                    submissions={submissions}
                    openVideoAssignmentCreation={openVideoAssignmentCreation}
                    openVideoFeedbackSettings={openVideoFeedbackSettings}
                    openVideoGradingSettings={openVideoGradingSettings}
                    {...props}
                />
            ),
        }[device];
    };

    return isLoading || !form || !submissions ? (
        <Loading />
    ) : (
        <>
            {renderResponsiveLayout(device)}
            <SelectRubricDialog
                open={selectRubricDialog.open}
                onClose={closeSelectRubricDialog}
                onClickAddRubric={handleClickAddRubric}
                onClickUseRubric={handleClickUseRubric}
            />
            <CreateRubricDialog
                open={createRubricDialog.open}
                onClose={closeCreateRubricDialog}
                onClickCreateAI={handleClickCreateAI}
                onClickCreateManual={handleClickCreateManual}
            />
            <CreateRubricAIDialog
                open={createRubricAIDialog.open}
                title={form.title}
                description={form.description}
                onClose={closeCreateRubricAIDialog}
                onCreate={handleSaveRubric}
            />
            <EditRubricDialog
                open={editRubricDialog.open}
                rubric={form.rubric}
                customDefaultForm={editRubricDialog.customDefaultForm}
                onClose={closeEditRubricDialog}
                onSave={handleSaveRubric}
            />
            <EditBaselineDialog
                open={editBaselineDialog.open}
                rubric={form.rubric}
                baseline={form.baseline}
                onClose={closeEditBaselineDialog}
                onSave={handleSaveBaseline}
            />
            <UploadContentBankResourcesDialog
                open={uploadContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeUploadContentBankResourcesDialog}
                onClick={onClickUploadContentBankResources}
            />
            <EditContentBankResourcesDialog
                open={editContentBankResourcesDialog.open}
                resources={form.contentBankResources}
                onClose={closeEditContentBankResourcesDialog}
                onSave={handleSaveContentBankResources}
            />
            <UploadPreviewDialog
                open={previewUploadDialog.open}
                assignment={form}
                onClose={closeUploadPreviewDialog}
                onClick={onClickUploadPreview}
            />
            <PreviewFeedbackDialog previewFeedbackDialog={previewFeedbackDialog} onClose={closePreviewFeedbackDialog} />
            <PreviewGradingDialog previewGradingDialog={previewGradingDialog} onClose={closePreviewGradingDialog} />
            <FeedbackSettingsDialog
                open={feedbackSettingsDialog.open}
                form={form}
                isFeedbackSettingsDefault={isFeedbackSettingsDefault}
                onClose={closeFeedbackSettingsDialog}
                onSave={handleSaveFeedbackSettings}
            />
            <EditGradingSettingsDialog
                open={gradingSettingsDialog.open}
                form={form}
                onClose={closeGradingSettingsDialog}
                onSave={handleSaveGradingSettings}
            />
            <VideoDialog open={videoDialog.open} videoId={videoDialog.videoId} onClose={closeVideoDialog} />
        </>
    );
};

export default PageLayout;
